import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import confetti from 'canvas-confetti'
import { useHunt, useWaypoints, useCheckin, useAnswerChallenge } from '../hooks/useHunt'
import { useGeolocation } from '../hooks/useGeolocation'
import { Button } from '../components/common/Button'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { ChallengeModal } from '../components/Challenge'
import { BadgeEarnedToast } from '../components/Badge'
import HuntMap from '../components/Map/HuntMap'
import type { WaypointWithClue, CheckinResponse, ChallengeAnswer, Location, Reward, NewlyEarnedBadge } from '../types'

// Confetti celebration effect
const triggerConfetti = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#f59e0b', '#d97706', '#fcd34d', '#fbbf24', '#78350f'],
  })
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: ['#f59e0b', '#d97706', '#fcd34d'],
    })
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: ['#f59e0b', '#d97706', '#fcd34d'],
    })
  }, 150)
}

// Check if we're in debug mode (only allow URL parameter in development)
const DEBUG_MODE = import.meta.env.DEV && new URLSearchParams(window.location.search).get('debug') === 'true'

// Format seconds as "M:SS"
function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.ceil(totalSeconds))
  const mins = Math.floor(safe / 60)
  const secs = safe % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Friendly text + emoji for reward claim position (1 = first to claim).
function formatClaimPosition(position: number): string {
  if (position === 1) return '🥇 You were the first to claim this reward!'
  if (position === 2) return '🥈 You were 2nd to claim this reward!'
  if (position === 3) return '🥉 You were 3rd to claim this reward!'
  return `You were #${position} to claim this reward!`
}

function HintsPanel({
  revealed,
  total,
  nextRevealAt,
  onNextRevealReached,
  expanded,
  onToggle,
}: {
  revealed: string[]
  total: number
  nextRevealAt?: string
  onNextRevealReached: () => void
  expanded: boolean
  onToggle: () => void
}) {
  const [, setTick] = useState(0)

  // Tick once a second only while we're waiting on a hint.
  useEffect(() => {
    if (!nextRevealAt) return
    const target = new Date(nextRevealAt).getTime()
    const interval = setInterval(() => {
      setTick((t) => t + 1)
      if (Date.now() >= target) {
        clearInterval(interval)
        onNextRevealReached()
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [nextRevealAt, onNextRevealReached])

  const revealedCount = revealed.length
  const remainingSeconds = nextRevealAt
    ? (new Date(nextRevealAt).getTime() - Date.now()) / 1000
    : 0

  return (
    <div className="mb-4">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <span className="font-medium">
          Hints ({revealedCount}/{total})
          {!expanded && nextRevealAt && (
            <span className="text-primary-500"> · next in {formatCountdown(remainingSeconds)}</span>
          )}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {revealed.map((text, i) => (
            <Card key={i} padding="md" className="bg-primary-50 border-primary-200">
              <p className="text-xs font-semibold text-primary-700 mb-1">
                Hint {i + 1}
              </p>
              <p className="text-brown-700 text-sm">{text}</p>
            </Card>
          ))}
          {revealedCount === 0 && !nextRevealAt && (
            <Card padding="md" className="bg-stone-50 border-stone-200">
              <p className="text-sm text-stone-500">No hints available yet.</p>
            </Card>
          )}
          {nextRevealAt && (
            <Card padding="md" className="bg-stone-50 border-stone-200">
              <p className="text-sm text-stone-600">
                Next hint in{' '}
                <span className="font-semibold text-primary-700">
                  {formatCountdown(remainingSeconds)}
                </span>
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

// Progress dots component matching Figma design
function ProgressDots({ total, completed, current }: { total: number; completed: number; current: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => {
        const isCompleted = i < completed
        const isCurrent = i === current
        return (
          <div
            key={i}
            className={`w-3 h-3 rounded-full transition-all ${
              isCompleted
                ? 'bg-primary-500'
                : isCurrent
                  ? 'bg-primary-400 ring-2 ring-primary-300 ring-offset-1 ring-offset-brown-900'
                  : 'bg-brown-700'
            }`}
          />
        )
      })}
    </div>
  )
}

export default function ActiveHunt() {
  const { huntId } = useParams<{ huntId: string }>()
  const { data: hunt, isLoading: huntLoading } = useHunt(huntId!)
  const { data: waypointsData, isLoading: waypointsLoading, refetch } = useWaypoints(huntId!)
  const checkinMutation = useCheckin()
  const answerMutation = useAnswerChallenge()
  const { location, requestLocation, loading: geoLoading, error: geoError, permissionDenied } = useGeolocation({ watch: true })

  const [showChallenge, setShowChallenge] = useState(false)
  const [challengeError, setChallengeError] = useState<string | null>(null)
  const [lastCheckin, setLastCheckin] = useState<CheckinResponse | null>(null)
  const [showReward, setShowReward] = useState(false)
  const [showHint, setShowHint] = useState(false)
  const [debugLocation, setDebugLocation] = useState<Location | null>(null)
  const [showWaypointComplete, setShowWaypointComplete] = useState(false)
  const [newBadge, setNewBadge] = useState<NewlyEarnedBadge | null>(null)

  // Get current waypoint
  const currentWaypoint = waypointsData?.waypoints.find(
    (wp) => wp.status === 'current'
  ) as WaypointWithClue | undefined

  const currentWaypointIndex = currentWaypoint?.order_index ?? 0

  // Debug logging
  if (DEBUG_MODE && currentWaypoint) {
    console.log('[DEBUG] Current waypoint:', currentWaypoint)
  }

  // Get effective location (debug or real)
  const effectiveLocation = debugLocation || location

  // Handle teleport (debug mode)
  const handleTeleport = () => {
    if (!currentWaypoint?.debug_location) return
    setDebugLocation({
      latitude: currentWaypoint.debug_location.lat,
      longitude: currentWaypoint.debug_location.lng,
      accuracy: 5,
    })
  }

  // Handle check-in
  const handleCheckin = async () => {
    if (!effectiveLocation || !currentWaypoint) return

    try {
      const result = await checkinMutation.mutateAsync({
        waypointId: currentWaypoint.id,
        location: {
          latitude: effectiveLocation.latitude,
          longitude: effectiveLocation.longitude,
          accuracy: effectiveLocation.accuracy,
        },
      })

      setLastCheckin(result)

      if (result.challenge) {
        setShowChallenge(true)
        setChallengeError(null)
      } else if (result.waypoint_completed) {
        setDebugLocation(null)
        triggerConfetti()
        setShowWaypointComplete(true)

        // Check for newly earned badges
        if (result.newly_earned_badges && result.newly_earned_badges.length > 0) {
          setNewBadge(result.newly_earned_badges[0])
        }

        if (result.reward) {
          setTimeout(() => {
            setShowWaypointComplete(false)
            setShowReward(true)
          }, 1500)
        } else {
          setTimeout(() => {
            setShowWaypointComplete(false)
            refetch()
          }, 1500)
        }
      }
    } catch (err) {
      console.error('Checkin failed:', err)
    }
  }

  // Handle challenge answer
  const handleAnswerChallenge = async (answer: ChallengeAnswer) => {
    if (!currentWaypoint) return

    setChallengeError(null)

    try {
      const result = await answerMutation.mutateAsync({
        waypointId: currentWaypoint.id,
        answer,
      })

      if (result.correct) {
        setShowChallenge(false)
        setDebugLocation(null)
        triggerConfetti()

        // Check for newly earned badges
        if (result.newly_earned_badges && result.newly_earned_badges.length > 0) {
          setNewBadge(result.newly_earned_badges[0])
        }

        if (result.reward) {
          setLastCheckin((prev) =>
            prev
              ? {
                  ...prev,
                  reward: result.reward as Reward,
                  reward_claim_position: result.reward_claim_position,
                }
              : null
          )
          setShowReward(true)
        }
        refetch()
      } else {
        setChallengeError(result.message || 'Incorrect answer. Try again!')
      }
    } catch (err) {
      console.error('Challenge answer failed:', err)
      setChallengeError('Something went wrong. Please try again.')
    }
  }

  // Check if hunt is complete
  useEffect(() => {
    if (hunt?.user_progress?.status === 'completed') {
      triggerConfetti()
    }
  }, [hunt?.user_progress?.status])

  // Loading state
  if (huntLoading || waypointsLoading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <Card padding="lg" className="text-center max-w-sm">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LoadingSpinner size="lg" />
          </div>
          <h3 className="font-semibold text-brown-900 mb-2">Loading Hunt...</h3>
          <p className="text-brown-600 text-sm">Preparing your adventure</p>
        </Card>
      </div>
    )
  }

  if (!hunt || !waypointsData) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
        <Card padding="lg" className="text-center">
          <h2 className="text-xl font-semibold text-brown-900 mb-2">Hunt not found</h2>
          <Link to="/hunts">
            <Button>Browse Hunts</Button>
          </Link>
        </Card>
      </div>
    )
  }

  const isComplete = hunt.user_progress?.status === 'completed'
  const isTooFar = lastCheckin && !lastCheckin.verified && lastCheckin.distance_meters > 0

  return (
    <div className="min-h-screen bg-cream-50 flex flex-col">
      {/* Header - Dark themed matching Figma */}
      <header className="bg-gradient-to-b from-brown-800 to-brown-900 text-white">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Close button */}
            <Link
              to={`/hunts/${huntId}`}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>

            {/* Title */}
            <h1 className="font-semibold text-cream-50 truncate px-4 text-center flex-1">
              {hunt.title}
            </h1>

            {/* Progress dots */}
            <ProgressDots
              total={waypointsData.total_waypoints}
              completed={waypointsData.completed_count}
              current={currentWaypointIndex}
            />
          </div>
        </div>
      </header>

      {/* Map */}
      <div className="flex-1 relative min-h-[40vh]">
        <HuntMap
          userLocation={effectiveLocation}
          waypointLocation={currentWaypoint?.debug_location}
          waypointRadius={currentWaypoint?.debug_location?.radius}
          className="w-full h-full"
        />

        {/* Map overlays for different states */}

        {/* Loading location state */}
        {!location && !debugLocation && geoLoading && (
          <div className="absolute inset-0 bg-cream-50/95 flex items-center justify-center p-4">
            <Card padding="lg" className="text-center max-w-sm">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LoadingSpinner size="lg" />
              </div>
              <h3 className="font-semibold text-brown-900 mb-2">Getting Location...</h3>
              <p className="text-brown-600 text-sm">
                Please wait while we find your position
              </p>
            </Card>
          </div>
        )}

        {/* Permission denied - show enable location button */}
        {!location && !debugLocation && permissionDenied && (
          <div className="absolute inset-0 bg-cream-50/95 flex items-center justify-center p-4">
            <Card padding="lg" className="text-center max-w-sm">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h3 className="font-semibold text-brown-900 mb-2">Location Permission Denied</h3>
              <p className="text-brown-600 text-sm mb-4">
                We need your location to verify waypoints. Please enable location access in your browser settings.
              </p>
              {geoError && <p className="text-red-600 text-sm mb-4">{geoError}</p>}
              <Button onClick={requestLocation}>
                Try Again
              </Button>
              {DEBUG_MODE && currentWaypoint?.debug_location && (
                <Button variant="outline" className="mt-2 w-full" onClick={handleTeleport}>
                  🔮 Teleport to Waypoint
                </Button>
              )}
            </Card>
          </div>
        )}

        {/* Waypoint icons on map edges - decorative */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-3">
          {waypointsData.waypoints.slice(0, 5).map((wp, i) => (
            <div
              key={wp.id}
              className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                wp.status === 'completed'
                  ? 'bg-primary-500 text-white'
                  : wp.status === 'current'
                    ? 'bg-white text-primary-600 ring-2 ring-primary-400'
                    : 'bg-white/80 text-brown-400'
              }`}
            >
              {wp.status === 'completed' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="font-bold text-sm">{i + 1}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom panel */}
      <div className="bg-white border-t border-cream-200 rounded-t-3xl -mt-6 relative z-10 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
        <div className="max-w-lg mx-auto px-4 py-6">

          {/* Hunt Complete state */}
          {isComplete ? (
            <div className="text-center py-4">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-brown-900 mb-2">Hunt Complete!</h3>
              <p className="text-brown-600 mb-6">
                Congratulations! You've finished all waypoints.
              </p>
              <div className="flex gap-3">
                <Link to="/profile" className="flex-1">
                  <Button className="w-full">View Rewards</Button>
                </Link>
                <Link to="/hunts" className="flex-1">
                  <Button variant="outline" className="w-full">Find More Hunts</Button>
                </Link>
              </div>
            </div>
          ) : currentWaypoint ? (
            <>
              {/* Waypoint indicator */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-brown-600 font-medium">
                  Waypoint {currentWaypoint.order_index + 1} of {waypointsData.total_waypoints}
                </span>
              </div>

              {/* Clue card - matching Figma with italic quoted text */}
              <Card variant="parchment" padding="md" className="mb-4">
                <p className="text-brown-800 italic leading-relaxed text-lg">
                  "{currentWaypoint.clue_text}"
                </p>
              </Card>

              {/* Progressive hints */}
              {currentWaypoint.total_hints > 0 && (
                <HintsPanel
                  revealed={currentWaypoint.revealed_hints}
                  total={currentWaypoint.total_hints}
                  nextRevealAt={currentWaypoint.next_hint_reveal_at}
                  onNextRevealReached={refetch}
                  expanded={showHint}
                  onToggle={() => setShowHint(!showHint)}
                />
              )}

              {/* Too far feedback */}
              {isTooFar && (
                <Card padding="md" className="mb-4 bg-red-50 border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-red-800">Too Far Away</p>
                      <p className="text-red-700 text-sm">
                        You're {Math.round(lastCheckin!.distance_meters)}m away. Get closer to check in!
                      </p>
                    </div>
                  </div>
                </Card>
              )}

              {/* Check-in button */}
              <Button
                className="w-full"
                size="lg"
                onClick={handleCheckin}
                loading={checkinMutation.isPending}
                disabled={!effectiveLocation}
              >
                <span className="flex items-center gap-2">
                  {effectiveLocation ? (
                    <>
                      <span>✨</span>
                      <span>I'm Here!</span>
                    </>
                  ) : (
                    'Waiting for location...'
                  )}
                </span>
              </Button>

              {/* Debug teleport button */}
              {DEBUG_MODE && currentWaypoint?.debug_location && (
                <Button variant="outline" className="w-full mt-2" onClick={handleTeleport}>
                  🔮 Teleport to Waypoint
                </Button>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <LoadingSpinner size="lg" className="mb-4" />
              <p className="text-brown-600">Loading waypoint...</p>
            </div>
          )}
        </div>
      </div>

      {/* Waypoint Complete overlay */}
      {showWaypointComplete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <Card padding="lg" className="text-center max-w-sm w-full animate-scale-in">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-brown-900 mb-2">Waypoint Complete!</h3>
            <p className="text-brown-600">Great job! Moving to the next clue...</p>
          </Card>
        </div>
      )}

      {/* Challenge modal */}
      {showChallenge && lastCheckin?.challenge && (
        <ChallengeModal
          challenge={lastCheckin.challenge}
          onSubmit={handleAnswerChallenge}
          onClose={() => setShowChallenge(false)}
          isSubmitting={answerMutation.isPending}
          error={challengeError || undefined}
          debugMode={DEBUG_MODE}
        />
      )}

      {/* Reward modal */}
      {showReward && lastCheckin?.reward && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <Card padding="lg" className="text-center max-w-sm w-full animate-scale-in">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-brown-900 mb-2">Reward Earned!</h3>
            {lastCheckin.reward_claim_position && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 px-3 py-2 text-sm font-semibold text-amber-800 shadow-sm">
                {formatClaimPosition(lastCheckin.reward_claim_position)}
              </div>
            )}
            <p className="text-primary-600 font-semibold text-lg mb-2">{lastCheckin.reward.name}</p>
            {lastCheckin.reward.description && (
              <p className="text-brown-600 text-sm mb-4">{lastCheckin.reward.description}</p>
            )}
            {lastCheckin.reward.code && (
              <div className="mb-4">
                <p className="text-xs text-brown-500 mb-1">Your promo code</p>
                <div className="flex items-center justify-center gap-2">
                  <span className="font-mono text-xl font-bold tracking-widest text-brown-900 bg-cream-100 border-2 border-dashed border-primary-300 rounded-lg px-4 py-2">
                    {lastCheckin.reward.code}
                  </span>
                  <button
                    onClick={() => navigator.clipboard.writeText(lastCheckin.reward!.code!)}
                    className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600"
                    title="Copy code"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-brown-400 mt-2">Saved to My Rewards — find it anytime in your profile</p>
              </div>
            )}
            <Button onClick={() => { setShowReward(false); setLastCheckin(null); refetch(); }}>
              Continue Adventure
            </Button>
          </Card>
        </div>
      )}

      {/* Badge earned toast */}
      {newBadge && (
        <BadgeEarnedToast
          badge={newBadge}
          onClose={() => setNewBadge(null)}
        />
      )}
    </div>
  )
}
